import type {
  PortCostDefaultId,
  ScenarioTimeline_ShipFragment,
} from '../../../graphql';
import type { LocationId } from '../../core/Core';
import {
  getDefaultPortCostByType,
  type PortCostEstimateMaps,
} from '../../hooks/usePortCostEstimates';

export type DefaultPortCostEstimates = {
  byName?: {
    id: PortCostDefaultId;
    currency: string;
    cost: number;
  };
  byTypes?: {
    id: PortCostDefaultId;
    currency: string;
    cost: number;
    typeName: string;
  }[];
  byClass?: {
    id: PortCostDefaultId;
    currency: string;
    cost: number;
    typeName: string;
  };
  byId?: { id: number; cost: number };
};

export function getPortCostDefaults(
  locationId: LocationId,
  portCostDefaultId: PortCostDefaultId | null | undefined,
  portCostEstimateMaps: PortCostEstimateMaps,
  vessel: ScenarioTimeline_ShipFragment,
) {
  const vesselId = vessel.id;
  const typeIds = vessel.vesselAttributesAssociationsByShipId.nodes ?? [];
  const vesselClass = vessel?.vesselAttributeByPrimaryAttributeId;

  const { defaultByTypeId, defaultByVesselId, defaultById, locationIdsByName } =
    portCostEstimateMaps;

  const updatedCost: DefaultPortCostEstimates = {};

  // Check for cost by vessel name
  const defByName = vesselId && defaultByVesselId.get(locationId, vesselId);
  if (defByName) {
    updatedCost.byName = {
      id: defByName.id,
      cost: defByName.cost,
      currency: defByName.currency,
    };
  }

  // Check for costs by vessel types
  for (const type of typeIds) {
    if (!type?.vesselAttributeId) continue;
    const defByType = getDefaultPortCostByType(
      defaultByTypeId,
      locationId,
      type.vesselAttributeId,
      locationIdsByName,
    );

    if (!defByType) continue;
    const existingTypes = updatedCost.byTypes ?? [];
    updatedCost.byTypes = [
      ...existingTypes,
      {
        ...defByType,
      },
    ];
  }

  // Check for cost by vessel class
  if (vesselClass) {
    const defByClass = getDefaultPortCostByType(
      defaultByTypeId,
      locationId,
      vesselClass.id,
      locationIdsByName,
    );
    if (defByClass) {
      updatedCost.byClass = {
        ...defByClass,
      };
    }
  }

  // Check for cost by port cost default ID
  if (portCostDefaultId) {
    const defById = defaultById.get(portCostDefaultId);
    if (defById) {
      updatedCost.byId = { id: portCostDefaultId, cost: defById };
    }
  }

  // applyPrioritiesToPortCosts expects undefined if no defaults were found
  if (Object.keys(updatedCost).length === 0) return undefined;

  return updatedCost;
}
